'use client';

import * as React from 'react';
import Link from 'next/link';
import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@workspace/ui/lib/utils';
import { routes } from '@workspace/routes';
import { ComingSoonModal } from '../coming-soon-modal';

export function Faq(): React.JSX.Element {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const [showComingSoon, setShowComingSoon] = React.useState(false);

  const faqs = [
    {
      question: 'Do I need any video production experience?',
      answer: 'Not at all! DAIRECTOR is designed for everyone. Just type what you want to create—like "upbeat music video in Tokyo" or "product commercial for sneakers"—and AI handles all the technical production. No editing or directing skills needed.',
    },
    {
      question: 'How long does it take to create a video?',
      answer: 'Most videos are ready in 2-5 minutes. A music video or commercial takes about 2-3 minutes, while a longer cinematic film might take up to 10 minutes. The AI creates everything simultaneously—visuals, dialogue, music, and editing.',
    },
    {
      question: 'What types of videos can I create?',
      answer: 'Anything you can imagine! Music videos, commercials, brand content, short films, social media ads, product demos, animated stories—even experimental art videos. Just describe your vision and the AI brings it to life with perfect cinematography.',
    },
    {
      question: 'Is it really free to start?',
      answer: 'Yes! Create your first 2 videos completely free—no credit card required. This gives you plenty of time to try different styles like music videos or commercials. After that, choose a plan that fits your creative needs.',
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0E0F1A]">
      <div className="container px-6">
        <div className="mx-auto max-w-3xl">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight leading-tight">
              Frequently asked questions
            </h2>
            <p className="text-lg lg:text-xl text-[#9EA1AE] leading-relaxed">
              Common questions about creating videos with AI
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="rounded-lg border border-[#4531FF]/20 bg-[#1A1B29]/80 backdrop-blur-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white">{faq.question}</span>
                  <ChevronDownIcon
                    className={cn(
                      'h-5 w-5 text-[#9EA1AE] transition-transform',
                      openIndex === index && 'rotate-180'
                    )}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-[#9EA1AE] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <p className="text-[#9EA1AE]">
              Have more questions?{' '}
              <button onClick={() => setShowComingSoon(true)} className="text-[#00C6FF] hover:underline">
                Try it free—make your first film
              </button>
            </p>
          </div>
        </div>
      </div>
      
      {/* Coming Soon Modal */}
      <ComingSoonModal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} />
    </section>
  );
}